import React, { useEffect } from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { fetchSystemMessages, SystemMessage } from "../../store/maintenanceSlice";
import styles from "./MaintenanceBanner.module.scss";

/**
 * MaintenanceBanner Component
 *
 * Displays system messages with ticker tape scrolling.
 * Supports multiple message types with different styling.
 *
 * API Integration:
 * - Endpoint: /system-messages
 * - Response: { result: [ { message_id, system_message, message_type, is_display } ] }
 * - Refresh: Every 5 minutes
 *
 * Message Types:
 * - ALERT: Critical/urgent messages with red styling
 * - WARNING: Important warnings with amber/orange styling
 * - INFO: Informational messages with teal styling
 *
 * Features:
 * - Redux state management
 * - Multiple messages support (stacked vertically)
 * - Ticker tape scrolling animation (pauses on hover)
 * - Type-based styling and icons
 * - Responsive design
 * - Accessible with ARIA labels
 */
const MaintenanceBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const { messages, loading } = useSelector(
    (state: RootState) => state.maintenance
  );

  useEffect(() => {
    // Fetch system messages on mount
    dispatch(fetchSystemMessages());

    // Set up periodic refresh (every 5 minutes)
    const intervalId = setInterval(() => {
      dispatch(fetchSystemMessages());
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [dispatch]);

  // Don't render during loading or when no messages
  if (loading || messages.length === 0) {
    return null;
  }

  const getIconForType = (type: SystemMessage["message_type"]) => {
    switch (type) {
      case "ALERT":
        return ErrorOutlineIcon;
      case "WARNING":
        return WarningAmberIcon;
      case "INFO":
        return InfoOutlinedIcon;
      default:
        return InfoOutlinedIcon;
    }
  };

  const getStyleClassForType = (type: SystemMessage["message_type"]) => {
    switch (type) {
      case "ALERT":
        return styles.maintenanceBannerAlert;
      case "WARNING":
        return styles.maintenanceBannerWarning;
      case "INFO":
        return styles.maintenanceBannerInfo;
      default:
        return styles.maintenanceBannerInfo;
    }
  };

  return (
    <div className={styles.maintenanceBannerContainer}>
      {messages.map((message) => {
        const Icon = getIconForType(message.message_type);
        const bannerClass = getStyleClassForType(message.message_type);

        return (
          <div
            key={message.message_id}
            className={`${styles.maintenanceBanner} ${bannerClass}`}
            role="alert"
            aria-live="polite"
            aria-atomic="true"
            aria-label={`${message.message_type} notification`}
          >
            <div className={styles.bannerContent}>
              <div className={styles.tickerContainer}>
                <div className={styles.tickerWrapper}>
                  {/* Duplicate content for seamless infinite scroll */}
                  <div className={styles.tickerContent}>
                    <Icon className={styles.icon} aria-hidden="true" />
                    <span className={styles.message}>{message.system_message}</span>
                  </div>
                  <div className={styles.tickerContent} aria-hidden="true">
                    <Icon className={styles.icon} />
                    <span className={styles.message}>{message.system_message}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MaintenanceBanner;
